import type { AccessQueueItemPublic, ItEmailMembershipPublic } from '@accounts/shared';

import { itemLabel } from '../accessFormat';

import { type ItTone, toneOfQueueType } from './tone';

/**
 * WHAT CAN GO IN AN EMAIL TO IT (spec row 15): open Needs access and Remove access work on an
 * OUTSIDE system — a suite app is granted in Accounts and never needs IT. `requested` is a
 * different step (the tracker's REQUESTED) and stays out, as the spec names only Needs access
 * and Remove access.
 */
export function isItWork(item: AccessQueueItemPublic): boolean {
  return (
    (item.type === 'needs_access' || item.type === 'remove_access') &&
    item.catalogItem.applicationId === null
  );
}

export interface PickItem {
  item: AccessQueueItemPublic;
  tone: ItTone;
}

export interface PickSystem {
  /** The catalog row id — the system the email groups by. */
  id: string;
  label: string;
  items: PickItem[];
  setUp: number;
  remove: number;
}

/**
 * The picker's shape: one block per system (A to Z by its full label), and under each, Set up
 * before Remove, then people A to Z — the order the drafted email uses, so what is picked
 * reads the same as what is written.
 */
export function pickerSystems(items: readonly AccessQueueItemPublic[]): PickSystem[] {
  const bySystem = new Map<string, PickSystem>();
  for (const item of items) {
    const tone = toneOfQueueType(item.type);
    if (tone === null || !isItWork(item)) continue;
    let system = bySystem.get(item.catalogItem.id);
    if (system === undefined) {
      system = {
        id: item.catalogItem.id,
        label: itemLabel(item.path, item.catalogItem.name),
        items: [],
        setUp: 0,
        remove: 0,
      };
      bySystem.set(item.catalogItem.id, system);
    }
    system.items.push({ item, tone });
    if (tone === 'setup') system.setUp += 1;
    else system.remove += 1;
  }
  const rank = (tone: ItTone) => (tone === 'setup' ? 0 : 1);
  return [...bySystem.values()]
    .map((system) => ({
      ...system,
      items: [...system.items].sort(
        (a, b) => rank(a.tone) - rank(b.tone) || a.item.user.name.localeCompare(b.item.user.name),
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Set up / Remove totals over any set of picked items. */
export function toneTotals(items: readonly PickItem[]): { setUp: number; remove: number } {
  const setUp = items.filter((entry) => entry.tone === 'setup').length;
  return { setUp, remove: items.length - setUp };
}

/**
 * WHERE EACH RECORD STANDS WITH IT (the server's membership read, SIR-7690). A record in a
 * SENT email has left its lane for Waiting on IT; one in a DRAFT stays in its lane, marked
 * "in a draft email". Either way it is in a live email, so the picker leaves it out: the
 * server refuses a record twice.
 */
export interface ItStanding {
  /** Record ids in a sent email: drawn under Waiting on IT, not in their lanes. */
  sent: ReadonlySet<string>;
  /** Record ids in a draft: still in their lanes. */
  draft: ReadonlySet<string>;
}

export function itStanding(memberships: readonly ItEmailMembershipPublic[]): ItStanding {
  const sent = new Set<string>();
  const draft = new Set<string>();
  for (const membership of memberships) {
    if (membership.emailStatus === 'sent') sent.add(membership.recordId);
    else draft.add(membership.recordId);
  }
  return { sent, draft };
}

/** A Queue item that is a record already in a sent email: Waiting on IT draws it instead. */
export function isWithIt(item: AccessQueueItemPublic, standing: ItStanding): boolean {
  return item.type !== 'request' && standing.sent.has(item.id);
}

/** Open IT work that can still go in a new email: in no live email, draft or sent. */
export function pickable(
  items: readonly AccessQueueItemPublic[],
  standing: ItStanding,
): AccessQueueItemPublic[] {
  return items.filter(
    (item) => isItWork(item) && !standing.sent.has(item.id) && !standing.draft.has(item.id),
  );
}
